export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-37K4SHL3WL'

type GoogleAnalyticsEventParams = Record<string, string | number | boolean | null | undefined>

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    doNotTrack?: string
  }
}

function normalizeText(value: unknown, maxLength = 120) {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, maxLength)
}

function isLocalAnalyticsHost(hostname: string) {
  const value = hostname.trim().toLowerCase()
  return value === 'localhost'
    || value === '127.0.0.1'
    || value === '0.0.0.0'
    || value === '::1'
    || value.endsWith('.localhost')
}

function isDoNotTrackEnabled() {
  if (typeof navigator === 'undefined') return false
  return navigator.doNotTrack === '1' || window.doNotTrack === '1'
}

export function isGoogleAnalyticsEnabled() {
  if (typeof window === 'undefined') return false
  if (isLocalAnalyticsHost(window.location.hostname)) return false
  if (isDoNotTrackEnabled()) return false
  return typeof window.gtag === 'function'
}

export function getGoogleAnalyticsPageTitle(pathname = typeof window !== 'undefined' ? window.location.pathname : '/') {
  if (pathname === '/') return 'Home'
  if (pathname === '/artikel') return 'Artikel Properti'
  if (pathname.startsWith('/artikel/')) return 'Detail Artikel Properti'
  if (pathname === '/jual' || pathname.startsWith('/jual/')) return 'Properti Dijual'
  if (pathname === '/sewa' || pathname.startsWith('/sewa/')) return 'Properti Disewa'
  if (pathname === '/properti-baru' || pathname.startsWith('/properti-baru/')) return 'Properti Baru'
  if (pathname === '/perumahan-baru' || pathname.startsWith('/perumahan-baru/')) return 'Perumahan Baru'
  if (pathname.startsWith('/listing/') || pathname.startsWith('/properti/')) return 'Detail Listing Properti'
  if (pathname.startsWith('/pasang-property')) return 'Pasang Properti'
  if (pathname.startsWith('/agent')) return 'Agent Properti'
  if (pathname === '/kpr/simulasi-kpr') return 'Simulasi KPR'
  if (pathname === '/kpr/syariah') return 'KPR Syariah'
  if (pathname === '/partner') return 'Partner'
  if (pathname === '/kompetitor') return 'Kompetitor'
  if (pathname === '/tentang-kami') return 'Tentang Kami'
  if (pathname === '/faq') return 'FAQ'
  if (pathname === '/privacy-policy') return 'Privacy Policy'
  if (pathname === '/terms-of-use') return 'Terms of Use'
  if (pathname === '/syarat-penggunaan-agen') return 'Syarat Penggunaan Agen'
  if (pathname === '/delete-account') return 'Delete Account'
  return document.title || 'Dihaw.co'
}

export function trackGoogleAnalyticsPageView(input: {
  path?: string
  title?: string
  location?: string
  referrer?: string
}) {
  if (!isGoogleAnalyticsEnabled()) return
  const path = input.path || `${window.location.pathname}${window.location.search}${window.location.hash}`
  window.gtag?.('event', 'page_view', {
    page_title: input.title || getGoogleAnalyticsPageTitle(window.location.pathname),
    page_location: input.location || window.location.href,
    page_path: path,
    page_referrer: input.referrer || document.referrer || undefined,
    send_to: GOOGLE_ANALYTICS_MEASUREMENT_ID,
  })
}

export function trackGoogleAnalyticsEvent(eventName: string, params: GoogleAnalyticsEventParams = {}) {
  if (!isGoogleAnalyticsEnabled()) return
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  )
  window.gtag?.('event', eventName, {
    ...cleanParams,
    send_to: GOOGLE_ANALYTICS_MEASUREMENT_ID,
  })
}

export function getElementTrackingLabel(element: Element) {
  const explicitLabel = element.getAttribute('data-ga-label') || element.getAttribute('aria-label')
  if (explicitLabel) return normalizeText(explicitLabel)
  const text = normalizeText(element.textContent)
  if (text) return text
  const title = element.getAttribute('title')
  return normalizeText(title || element.tagName.toLowerCase())
}

export function getOutboundLinkEventName(href: string) {
  const normalized = href.trim().toLowerCase()
  if (normalized.startsWith('mailto:')) return 'email_click'
  if (normalized.startsWith('tel:')) return 'phone_click'
  if (normalized.includes('wa.me/') || normalized.includes('whatsapp.com/') || normalized.includes('api.whatsapp.com/')) {
    return 'whatsapp_click'
  }
  return 'outbound_click'
}

export function isDownloadHref(href: string) {
  return /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|csv|png|jpe?g|webp)(?:$|[?#])/i.test(href)
}
